import React, { useRef } from 'react';
import { toast } from 'react-toastify';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useUploadFiles } from './hooks/fileMutations';
import { FileScopeType } from './hooks/useGetAllFiles';
import isRoleReadOnly from 'PREPAREsrc/utils/isRoleReadOnly';
import {
  getAcceptString,
  getUserFacingFormatsDisplay,
} from '../DisasterGPT/DisasterGPTConstants';

interface Props {
  ownerId?: string;
  userWorkspaceRoleName?: string;
  className?: string;
  onFilesUploaded?: () => void;
  showNotificationText?: boolean;
  showTooltip?: boolean;
  notefolderId?: string | null;
  scopeType?: FileScopeType;
  // Compact icon-only rendering for dense headers (per-section row in
  // ChatFileSelector). Wraps the icon in a hover tooltip.
  iconOnly?: boolean;
  iconLabel?: string;
}

const UploadFilesButton: React.FC<Props> = ({
  ownerId,
  userWorkspaceRoleName,
  className = 'button--primary w-100 w-md-auto mb-1 mb-md-0',
  onFilesUploaded,
  showNotificationText = true,
  showTooltip = true,
  notefolderId,
  scopeType,
  iconOnly = false,
  iconLabel = 'Upload files',
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { mutateAsync: uploadFiles } = useUploadFiles();

  const onUploadClick = () => fileInputRef.current?.click();

  const changeHandler = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length <= 0) return;
    if (files.length > 10) {
      toast.info('Maximum upload limit is 10 files at a time');
      return;
    }

    try {
      await uploadFiles({
        files: Array.from(files),
        notefolderId,
        workspaceId: ownerId, // Use the ownerId prop as workspaceId
        scopeType,
      });
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Callback after successful upload
      if (onFilesUploaded) {
        onFilesUploaded();
      }
    } catch (_) {
      // error handled by hooks/toasts
    }
  };

  if (isRoleReadOnly(userWorkspaceRoleName)) {
    return null;
  }

  const supportedFormatsTooltip = (
    <Tooltip id="supported-formats-tooltip">
      Supported formats: {getUserFacingFormatsDisplay()}
    </Tooltip>
  );

  if (iconOnly) {
    return (
      <>
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="upload-files-icon-tooltip">{iconLabel}</Tooltip>
          }
        >
          <button
            type="button"
            onClick={onUploadClick}
            className="btn btn-link p-1 border-0 text-white"
            aria-label={iconLabel}
          >
            <i className="material-symbols-outlined">upload_file</i>
          </button>
        </OverlayTrigger>
        <input
          ref={fileInputRef}
          type="file"
          name="file"
          multiple
          accept={getAcceptString()}
          style={{ display: 'none' }}
          onChange={changeHandler}
        />
      </>
    );
  }

  return (
    <>
      <div className="d-flex align-items-center">
        <StylishNewButton
          className={className}
          onClick={onUploadClick}
          type="button"
        >
          Upload Files
        </StylishNewButton>
        {showTooltip && (
          <OverlayTrigger placement="top" overlay={supportedFormatsTooltip}>
            <i
              className="fa fa-info-circle ms-2"
              style={{ cursor: 'pointer', fontSize: '16px' }}
              aria-label="Supported file formats"
            />
          </OverlayTrigger>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        name="file"
        multiple
        accept={getAcceptString()}
        style={{ display: 'none' }}
        onChange={changeHandler}
      />
      {showNotificationText && (
        <span className="notifyMessage weight-500 ms-sm-0 ms-md-auto ms-lg-4">
          *Maximum upload limit is 10 files at a time
        </span>
      )}
    </>
  );
};

export default UploadFilesButton;
